import React from 'react';
import { Badge, Card, Stack } from 'react-bootstrap';
import { BsTagFill } from 'react-icons/bs';
import { LabelTextSpread } from '../../../../../components/generic';
import CurrencyCell from '../../../../../components/tables/cellComponents/CurrencyCell';
import { OrderPlusCost, RegistrationPricingLine } from '../../../../../types/orders.type';
import { getSessionBadgeClass } from '../../../../../utils/sessionColors';

interface OrderCostProps {
  order: OrderPlusCost;
  className?: string;
  index?: number;
}

const strikeStyle = { color: 'var(--bs-sfc-pink)', fontSize: '.75rem' };

const StudentLine: React.FC<{ line: RegistrationPricingLine }> = ({ line }) => {
  const showStrike = line.due < line.price;

  return (
    <LabelTextSpread
      fontSize=".75rem"
      label={line.childName}
    >
      <Stack
        direction="horizontal"
        gap={1}
        className="justify-content-end flex-wrap"
      >
        {showStrike && (
          <s style={strikeStyle}>
            <CurrencyCell value={line.price} />
          </s>
        )}
        <CurrencyCell value={line.due} />
      </Stack>
    </LabelTextSpread>
  );
};

export const OrderCost: React.FC<OrderCostProps> = ({ order, className, index }) => {
  const { price, fullPrice, discount, registrationLines } = order.cost;
  const discountNum = discount ?? 0;
  const prorated = price !== fullPrice;
  const discountPrice =
    discountNum > 0 ? roundMoney(price * (1 - discountNum)) : roundMoney(price);
  const count = order.registrations.length;
  const showStudentLines = (registrationLines?.length ?? 0) > 0;

  const totalLabel = (
    <Badge className={`text-uppercase ${getSessionBadgeClass(index ?? 0)}`}>
      Session {(index ?? 0) + 1}
    </Badge>
  );

  const discounted = prorated || discountNum > 0;
  let totalContent = <CurrencyCell value={discountPrice} />;

  if (discounted && !showStudentLines) {
    totalContent = (
      <Stack
        direction="horizontal"
        gap={1}
      >
        <s style={strikeStyle}>
          <CurrencyCell value={fullPrice} />
        </s>
        <CurrencyCell value={discountPrice} />
      </Stack>
    );
  }

  let sessionBadges: React.ReactNode = null;
  if (discounted && !showStudentLines && discountNum > 0) {
    sessionBadges = (
      <Stack
        direction="horizontal"
        gap={1}
      >
        <Badge>
          <BsTagFill /> -{Math.round(discountNum * 1000) / 10}%
        </Badge>
        {prorated && <Badge>Prorated</Badge>}
      </Stack>
    );
  } else if (discounted && !showStudentLines && prorated) {
    sessionBadges = <Badge>Prorated</Badge>;
  }

  const eachPriceFull = count > 0 ? fullPrice / count : 0;
  const eachPrice = count > 0 ? discountPrice / count : 0;
  const eachContent =
    discounted && !showStudentLines ? (
      <Stack
        direction="horizontal"
        gap={1}
      >
        <s style={strikeStyle}>
          <CurrencyCell
            value={eachPriceFull}
            label="each"
          />
        </s>
        <CurrencyCell
          value={eachPrice}
          label="each"
        />
      </Stack>
    ) : (
      <CurrencyCell
        value={eachPrice}
        label="each"
      />
    );

  return (
    <Card>
      <Card.Body className="p-1">
        <Stack className={className}>
          <LabelTextSpread
            opacity={1}
            label={
              sessionBadges ? (
                <Stack
                  direction="horizontal"
                  gap={1}
                  className="flex-wrap"
                >
                  {totalLabel}
                  {sessionBadges}
                </Stack>
              ) : (
                totalLabel
              )
            }
          >
            {totalContent}
          </LabelTextSpread>

          {showStudentLines &&
            registrationLines?.map((line) => (
              <StudentLine
                key={line.childId}
                line={line}
              />
            ))}

          {count > 0 && !showStudentLines && (
            <LabelTextSpread
              fontSize=".75rem"
              label={`x${count} Students`}
            >
              {eachContent}
            </LabelTextSpread>
          )}
        </Stack>
      </Card.Body>
    </Card>
  );
};

function roundMoney(amount: number): number {
  return Math.round(amount * 100) / 100;
}
